import { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { useAuth } from "../../../../hooks";
import { PatientReviApi } from "../../../../api/revi/patientApi";

const api = new PatientReviApi();

export function useTherapistPacientesController() {
  const { accessToken } = useAuth();

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    totalDocs: 0,
    totalPages: 1,
  });

  const [filters, setFilters] = useState({ search: "", sexo: null });

  // ---- Modal state ----
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("detail");
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [selectedPatientLite, setSelectedPatientLite] = useState(null);

  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [patientProfile, setPatientProfile] = useState(null);

  // Debounce para search
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search), 350);
    return () => clearTimeout(t);
  }, [filters.search]);

  const load = useCallback(
    async ({ page = 1 } = {}) => {
      if (!accessToken) return;

      try {
        setLoading(true);
        const res = await api.getMyForTherapist(accessToken, {
          page,
          limit: pagination.limit,
          search: debouncedSearch,
          sexo: filters.sexo || undefined,
        });

        setPatients(res.patients || []);
        setPagination((prev) => ({
          ...prev,
          page: res.pagination?.page || page,
          limit: res.pagination?.limit || prev.limit,
          totalDocs: res.pagination?.totalDocs || 0,
          totalPages: res.pagination?.totalPages || 1,
        }));
      } catch (e) {
        console.error(e);
        message.error(e.message || "Error al cargar usuarios");
      } finally {
        setLoading(false);
      }
    },
    [accessToken, debouncedSearch, filters.sexo, pagination.limit],
  );

  useEffect(() => {
    load({ page: 1 });
  }, [load]);

  const onSearch = useCallback((v) => {
    setFilters((prev) => ({ ...prev, search: v }));
  }, []);

  const onSexo = useCallback((v) => {
    setFilters((prev) => ({ ...prev, sexo: v || null }));
  }, []);

  const onPageChange = useCallback(
    (page) => {
      load({ page });
    },
    [load],
  );

  // ---- Modal handlers ----
  const openPatientModal = useCallback(
    (patientId, tab = "detail") => {
      if (!patientId) return;

      setIsModalOpen(true);
      setActiveTab(tab);
      setSelectedPatientId(patientId);

      // Lite data (para header inmediato)
      const lite = patients.find((p) => p._id === patientId) || null;
      setSelectedPatientLite(lite);

      // Reset profile state
      setPatientProfile(null);
      setProfileError("");
    },
    [patients],
  );

  const closePatientModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedPatientId(null);
    setSelectedPatientLite(null);
    setPatientProfile(null);
    setProfileError("");
    setProfileLoading(false);
    setActiveTab("detail");
  }, []);

  const loadPatientProfile = useCallback(async () => {
    if (!accessToken || !selectedPatientId || !isModalOpen) return;

    try {
      setProfileLoading(true);
      setProfileError("");
      const res = await api.getProfile(accessToken, selectedPatientId);
      setPatientProfile(res.patient || null);
    } catch (e) {
      console.error(e);
      setProfileError(e.message || "No se pudo cargar la ficha");
    } finally {
      setProfileLoading(false);
    }
  }, [accessToken, isModalOpen, selectedPatientId]);

  // Cargar ficha al abrir modal o cambiar paciente
  useEffect(() => {
    loadPatientProfile();
  }, [loadPatientProfile]);

  const selectedPatient = patientProfile || selectedPatientLite;

  const data = useMemo(
    () => ({
      patients,
      loading,
      pagination,
      filters,
      isModalOpen,
      activeTab,
      selectedPatientId,
      selectedPatient,
      profileLoading,
      profileError,
    }),
    [
      patients,
      loading,
      pagination,
      filters,
      isModalOpen,
      activeTab,
      selectedPatientId,
      selectedPatient,
      profileLoading,
      profileError,
    ],
  );

  const updateClinicalInfoLocal = useCallback((clinicalInfo) => {
    setPatientProfile((prev) =>
      prev ? { ...prev, clinical_info: clinicalInfo } : prev,
    );
  }, []);

  return {
    ...data,
    onSearch,
    onSexo,
    onPageChange,
    openPatientModal,
    closePatientModal,
    setActiveTab,
    updateClinicalInfoLocal,
  };
}
